import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, Plus, Copy, RefreshCw, Trash2, Eye, EyeOff, 
  Mail, Phone, Building2, Calendar, Download, Key, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProspectionPage() {
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [newAccess, setNewAccess] = useState({
    email: '',
    nom: '',
    prenom: '',
    entreprise: '',
    telephone: '',
    notes: '',
    societe_type: 'EARL'
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadAccesses();
  }, []);

  const loadAccesses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospection/accesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccesses(data.accesses || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAccess = async () => {
    if (!newAccess.email || !newAccess.nom || !newAccess.prenom || !newAccess.entreprise) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/prospection/create-access`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAccess)
      });

      if (res.ok) {
        const data = await res.json();
        setCreatedCredentials(data.credentials);
        toast.success('Accès créé avec succès !');
        loadAccesses();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const regeneratePassword = async (accessId) => {
    try {
      const res = await fetch(`${API_URL}/api/prospection/accesses/${accessId}/regenerate-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Nouveau mot de passe : ${data.new_password}`);
        loadAccesses();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const togglePasswordVisibility = (id) => {
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusBadge = (statut) => {
    const styles = {
      actif: 'bg-green-100 text-green-800',
      désactivé: 'bg-red-100 text-red-800',
      supprimé: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[statut] || styles.actif}>{statut || 'actif'}</Badge>;
  };

  const exportCSV = async () => {
    window.open(`${API_URL}/api/prospection/export?format=csv`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="prospection-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Suivi Prospection
          </h1>
          <p className="text-muted-foreground">
            Gérez les accès envoyés aux prospects et clients
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exporter
          </Button>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary">
                <Plus className="w-4 h-4 mr-2" />
                Créer un accès
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer un nouvel accès prospect</DialogTitle>
              </DialogHeader>
              
              {createdCredentials ? (
                <div className="space-y-4 py-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Accès créé avec succès !</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="text-sm text-gray-600">URL</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{createdCredentials.url}</code>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdCredentials.url)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="text-sm text-gray-600">Email</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{createdCredentials.email}</code>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdCredentials.email)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="text-sm text-gray-600">Mot de passe</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded font-bold text-primary">{createdCredentials.password}</code>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdCredentials.password)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      setCreatedCredentials(null);
                      setNewAccess({
                        email: '', nom: '', prenom: '', entreprise: '',
                        telephone: '', notes: '', societe_type: 'EARL'
                      });
                      setShowDialog(false);
                    }}
                  >
                    Fermer
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom *</Label>
                      <Input
                        value={newAccess.prenom}
                        onChange={(e) => setNewAccess({ ...newAccess, prenom: e.target.value })}
                        placeholder="Jean"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={newAccess.nom}
                        onChange={(e) => setNewAccess({ ...newAccess, nom: e.target.value })}
                        placeholder="Dupont"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newAccess.email}
                      onChange={(e) => setNewAccess({ ...newAccess, email: e.target.value })}
                      placeholder="jean.dupont@exemple.fr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Entreprise / Exploitation *</Label>
                    <Input
                      value={newAccess.entreprise}
                      onChange={(e) => setNewAccess({ ...newAccess, entreprise: e.target.value })}
                      placeholder="EARL Les Vignes"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={newAccess.telephone}
                        onChange={(e) => setNewAccess({ ...newAccess, telephone: e.target.value })}
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type société</Label>
                      <Select
                        value={newAccess.societe_type}
                        onValueChange={(v) => setNewAccess({ ...newAccess, societe_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EARL">EARL</SelectItem>
                          <SelectItem value="SCEA">SCEA</SelectItem>
                          <SelectItem value="GAEC">GAEC</SelectItem>
                          <SelectItem value="ETA">ETA</SelectItem>
                          <SelectItem value="CUMA">CUMA</SelectItem>
                          <SelectItem value="Individuel">Individuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={newAccess.notes}
                      onChange={(e) => setNewAccess({ ...newAccess, notes: e.target.value })}
                      placeholder="Notes sur le prospect..."
                      rows={2}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDialog(false)}>
                      Annuler
                    </Button>
                    <Button onClick={createAccess}>
                      Créer l'accès
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-2xl font-bold">{accesses.length}</span>
            </div>
            <p className="text-xs text-muted-foreground">Total accès</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold">{accesses.filter(a => a.statut === 'actif' || !a.statut).length}</span>
            </div>
            <p className="text-xs text-muted-foreground">Actifs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold">
                {accesses.filter(a => {
                  if (!a.created_at) return false;
                  const created = new Date(a.created_at);
                  const now = new Date();
                  return (now - created) / (1000 * 60 * 60 * 24) <= 7;
                }).length}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Cette semaine</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              <span className="text-2xl font-bold">{accesses.filter(a => a.last_login).length}</span>
            </div>
            <p className="text-xs text-muted-foreground">Connectés</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des accès</CardTitle>
          <CardDescription>Tous les accès envoyés aux prospects</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mot de passe</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucun accès créé. Cliquez sur "Créer un accès" pour commencer.
                  </TableCell>
                </TableRow>
              ) : (
                accesses.map((access) => (
                  <TableRow key={access.id}>
                    <TableCell className="text-sm">
                      {access.created_at ? new Date(access.created_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{access.entreprise}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {access.prenom} {access.nom}
                      {access.telephone && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {access.telephone}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-muted px-2 py-0.5 rounded">{access.email}</code>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(access.email)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-muted px-2 py-0.5 rounded">
                          {showPassword[access.id] ? access.temp_password : '••••••••'}
                        </code>
                        <Button size="sm" variant="ghost" onClick={() => togglePasswordVisibility(access.id)}>
                          {showPassword[access.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(access.temp_password)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(access.statut)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => regeneratePassword(access.id)}
                        title="Régénérer mot de passe"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
